import { ApiError } from '@/lib/api';
import { isInternalPath } from '@/lib/internal-path';

import type { Channel, EscalationTarget, InboxItem, NotificationRule, RepeatEvery } from './types';
import { errorAdvice } from '@/lib/error-advice';

// Textos y reglas de presentación de la pantalla Notificaciones (so=370). Puro.

export interface Badge {
  cls: 'b-ok' | 'b-warn' | 'b-red' | 'b-info' | 'b-mut';
  label: string;
}

const days = (n: number): string => (n === 1 ? '1 día' : `${n} días`);

export function channelLabel(channels: Channel[]): string {
  const app = channels.includes('in_app');
  const email = channels.includes('email');
  if (app && email) {
    return 'Correo + plataforma';
  }
  if (app) {
    return 'Plataforma';
  }
  if (email) {
    return 'Correo';
  }
  return 'Sin canal';
}

const REPEAT: Record<RepeatEvery, string> = {
  daily: 'cada día',
  weekly: 'cada semana',
  biweekly: 'cada quincena',
  monthly: 'cada mes',
};

export function repeatLabel(r: RepeatEvery): string {
  return REPEAT[r];
}

const TARGET: Record<EscalationTarget, string> = {
  department_supervisors: 'Supervisores del departamento',
  quality: 'Calidad',
  none: 'Sin escalamiento',
};

export function escalationTargetLabel(t: EscalationTarget): string {
  return TARGET[t];
}

/** Días de gracia tras el vencimiento antes de escalar, en palabras. */
export function escalateAfterLabel(n: number): string {
  if (n === 0) {
    return 'el día que vence';
  }
  return n === 1 ? 'a 1 día de vencida' : `a los ${n} días de vencida`;
}

/** Columna "Anticipación": cuándo dispara la regla. El escalamiento toma sus
 *  parámetros de la regla de etapa vencida (así los guarda el backend). */
export function leadLabel(rule: NotificationRule, overdue: NotificationRule): string {
  switch (rule.code) {
    case 'stage.pending':
      return 'al entrar a la etapa';
    case 'stage.half_elapsed':
      return `al ${rule.thresholdPct ?? 50} % del plazo`;
    case 'stage.overdue':
      return `al vencer · ${repeatLabel(rule.repeatEvery ?? 'daily')}`;
    case 'escalation':
      return overdue.escalationTarget === 'none'
        ? 'sin escalamiento'
        : escalateAfterLabel(overdue.escalateAfterDays ?? 2);
    case 'review.upcoming':
      return `${days(rule.leadDays ?? 90)} antes · ${repeatLabel(rule.repeatEvery ?? 'monthly')}`;
    case 'custom_date.upcoming':
      return 'según los días de aviso de cada campo de fecha';
    case 'custom_date.overdue':
      return 'al día siguiente de la fecha, una vez, en los campos que lo piden';
    // G3
    case 'request.submitted':
      return 'al recibir la solicitud';
    case 'request.resolved':
      return 'al aprobarla o rechazarla';
    // G2-C
    case 'training.lms_unreported':
      return 'tras un día sin reporte del LMS';
  }
}

const STAGES: Record<string, string> = {
  elaboracion: 'Elaboración',
  revision: 'Revisión',
  aprobacion: 'Aprobación',
  capacitacion: 'Capacitación',
};

export function stageLabel(stage: string | null): string {
  return (stage !== null && STAGES[stage]) || 'Etapa';
}

/** Días de vencida de una etapa escalada (siempre vencida: 0 = venció hoy). */
export function overdueDaysLabel(daysLeft: number): string {
  return daysLeft >= 0 ? 'vencida hoy' : `vencida hace ${days(-daysLeft)}`;
}

/** Ruta interna a la que lleva un aviso. Solo rutas de esta app: relativas que empiezan
 *  con una sola barra o absolutas del mismo origen. Cualquier otra cosa no se navega. */
export function safeActionPath(url: string | null, origin: string): string | null {
  if (!url) {
    return null;
  }
  if (url.startsWith('/')) {
    return isInternalPath(url) ? url : null;
  }
  try {
    const u = new URL(url);
    const path = `${u.pathname}${u.search}${u.hash}`;
    return u.origin === origin && isInternalPath(path) ? path : null;
  } catch {
    return null;
  }
}

export function inboxBadges(item: InboxItem): Badge[] {
  const out: Badge[] = [];
  if (item.escalationLevel > 0) {
    out.push({ cls: 'b-red', label: 'Escalado' });
  }
  if (item.dismissedAt !== null) {
    out.push({ cls: 'b-mut', label: 'Descartado' });
  }
  if (item.actedAt !== null) {
    out.push({ cls: 'b-ok', label: 'Completada' });
  } else if (item.pendingAction) {
    out.push({ cls: 'b-warn', label: 'Pendiente de acción' });
  }
  return out;
}

export function settingsErrorCopy(err: unknown): string {
  if (err instanceof ApiError && err.status === 403) {
    return 'Ya no tienes permiso para configurar notificaciones.';
  }
  if (err instanceof ApiError && err.status === 400) {
    return 'Revisa los valores: alguno está fuera del rango permitido.';
  }
  return `No se pudo guardar. ${errorAdvice(err)}`;
}

export function dismissErrorCopy(err: unknown): string {
  if (err instanceof ApiError && err.status === 409) {
    return 'Este aviso no se puede descartar hasta completar la actividad.';
  }
  if (err instanceof ApiError && err.status === 404) {
    return 'El aviso ya no existe o no pertenece a la empresa activa.';
  }
  return `No se pudo actualizar el aviso. ${errorAdvice(err)}`;
}

/** Confirmación de "Marcar todos como leídos". */
export function markedAllCopy(n: number): string {
  if (n <= 0) {
    return 'No había avisos sin leer';
  }
  return n === 1 ? '1 aviso marcado como leído' : `${n} avisos marcados como leídos`;
}

export function markAllErrorCopy(err: unknown): string {
  return `No se pudieron marcar los avisos como leídos. ${errorAdvice(err)}`;
}
